import type { CSSProperties } from "react";

import { ReportMarkdown } from "@/components/ui/report/ReportMarkdown";
import { useReportTheme } from "@/components/ui/report/ReportThemeProvider";
import {
  type ReportHighlightPresentation,
  resolveReportHighlightPresentation,
  resolveReportSpacingStyle,
  resolveReportTextStyle,
} from "@/components/ui/report/reportPresentation";
import { REPORT_CSS_VAR_PRIMARY } from "@/components/ui/report/reportTheme";
import { cn } from "@/lib/utils";

/** Shown when no highlight title is provided. */
export const REPORT_HIGHLIGHT_DEFAULT_TITLE = "Today at a Glance";
const LEFT_ACCENT_WIDTH_PX = 4;

export interface ReportHighlightProps extends ReportHighlightPresentation {
  /** Defaults to "Today at a Glance" when omitted or empty. */
  title?: string | null;
  content: string;
  className?: string;
}

/** Highlight callout for executive summaries (e.g. Today at a Glance). */
export const ReportHighlight = ({
  title,
  content,
  className,
  ...presentation
}: ReportHighlightProps) => {
  const theme = useReportTheme();
  const {
    borderColor,
    backgroundColor,
    accentWidthPx,
    titleStyle,
    contentStyle,
    spacing,
  } = resolveReportHighlightPresentation(theme?.reportHighlight, presentation);
  if (content == null || content.trim() === "") {
    return null;
  }

  const displayTitle =
    title != null && title.trim() !== ""
      ? title.trim()
      : REPORT_HIGHLIGHT_DEFAULT_TITLE;

  const resolvedAccentColor =
    borderColor != null && borderColor !== ""
      ? borderColor
      : `var(${REPORT_CSS_VAR_PRIMARY})`;
  const resolvedBackgroundColor =
    backgroundColor != null && backgroundColor !== ""
      ? backgroundColor
      : `color-mix(in srgb, var(${REPORT_CSS_VAR_PRIMARY}) 8%, white)`;

  const { gapPx: titleContentGapPx, ...boxPadding } = spacing ?? {};
  const chromeStyle: CSSProperties = {
    borderLeftWidth: accentWidthPx ?? LEFT_ACCENT_WIDTH_PX,
    borderLeftStyle: "solid",
    borderLeftColor: resolvedAccentColor,
    backgroundColor: resolvedBackgroundColor,
    ...resolveReportSpacingStyle(boxPadding),
  };

  const resolvedTitleStyle: CSSProperties = {
    color: resolvedAccentColor,
    ...(titleContentGapPx != null && { marginBottom: titleContentGapPx }),
    ...resolveReportTextStyle(titleStyle),
  };

  return (
    <section
      className={cn("py-3 pl-4 pr-4 rounded-xs", className)}
      style={chromeStyle}
    >
      <h2
        className="m-0 mb-2 text-sm font-semibold leading-relaxed"
        style={resolvedTitleStyle}
      >
        {displayTitle}
      </h2>
      <ReportMarkdown textStyle={contentStyle}>{content}</ReportMarkdown>
    </section>
  );
};
